import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Gift, Users, CheckCircle, Sparkles, Copy, Check, Link as LinkIcon, Share2, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { AuthenticatedHeader } from '@/components/layout/AuthenticatedHeader';
import { Footer } from '@/components/layout/Footer';
import { ReferralShareDialog } from '@/components/sharing/ReferralShareDialog';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import peaceBg from '@/assets/peace-clarity-bg.png';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const referCopy = {
  en: { badge: 'SPREAD THE LOVE', title: 'Tell a Friend', desc: 'Share your personal link. When a friend signs up and purchases any bundle, you\'ll <strong>both</strong> receive a bonus announcement credit.', yourLink: 'Your Referral Link', copyLink: 'Copy Link', copied: 'Copied!', share: 'Share', scanQR: 'Scan to open the signup link', bonus: 'Bonus applies when your friend signs up using this link and purchases any paid bundle.', invited: 'Friends Invited', bonusEarned: 'Bonus Credits Earned', pending: 'Pending Invites', howTitle: 'How It Works', step1: 'Share', step1d: 'Copy your unique link and send it to a friend.', step2: 'They Purchase', step2d: 'When your friend signs up and buys any bundle, the magic happens.', step3: 'Both Earn', step3d: 'You both receive 1 bonus announcement credit. Free!', yourReferrals: 'Your Referrals', bonusEarnedBadge: 'Bonus Earned', pendingBadge: 'Pending', toastCopied: 'Link copied. Share it with someone you care about.' },
  es: { badge: 'COMPARTE EL AMOR', title: 'Invita a un Amigo', desc: 'Comparte tu enlace personal. Cuando un amigo se registre y compre cualquier paquete, <strong>ambos</strong> recibirán un crédito de anuncio extra.', yourLink: 'Tu Enlace de Referencia', copyLink: 'Copiar Enlace', copied: '¡Copiado!', share: 'Compartir', scanQR: 'Escanea para abrir el enlace de registro', bonus: 'El bono aplica cuando tu amigo se registra con este enlace y compra cualquier paquete.', invited: 'Amigos Invitados', bonusEarned: 'Créditos Extra Ganados', pending: 'Invitaciones Pendientes', howTitle: 'Cómo Funciona', step1: 'Comparte', step1d: 'Copia tu enlace único y envíalo a un amigo.', step2: 'Ellos Compran', step2d: 'Cuando tu amigo se registra y compra un paquete, la magia sucede.', step3: 'Ambos Ganan', step3d: '¡Ambos reciben 1 crédito de anuncio extra. Gratis!', yourReferrals: 'Tus Referencias', bonusEarnedBadge: 'Bono Ganado', pendingBadge: 'Pendiente', toastCopied: 'Enlace copiado. Compártelo con alguien que te importa.' },
  fr: { badge: 'PARTAGEZ L\'AMOUR', title: 'Parlez-en à un Ami', desc: 'Partagez votre lien personnel. Quand un ami s\'inscrit et achète un forfait, vous recevrez <strong>tous les deux</strong> un crédit d\'annonce bonus.', yourLink: 'Votre Lien de Parrainage', copyLink: 'Copier le Lien', copied: 'Copié !', share: 'Partager', scanQR: 'Scannez pour ouvrir le lien d\'inscription', bonus: 'Le bonus s\'applique lorsque votre ami s\'inscrit via ce lien et achète un forfait.', invited: 'Amis Invités', bonusEarned: 'Crédits Bonus Gagnés', pending: 'Invitations en Attente', howTitle: 'Comment ça Marche', step1: 'Partagez', step1d: 'Copiez votre lien unique et envoyez-le à un ami.', step2: 'Ils Achètent', step2d: 'Quand votre ami s\'inscrit et achète un forfait, la magie opère.', step3: 'Vous Gagnez Tous les Deux', step3d: 'Vous recevez chacun 1 crédit d\'annonce bonus. Gratuit !', yourReferrals: 'Vos Parrainages', bonusEarnedBadge: 'Bonus Gagné', pendingBadge: 'En attente', toastCopied: 'Lien copié. Partagez-le avec quelqu\'un qui compte pour vous.' },
  pt: { badge: 'ESPALHE O AMOR', title: 'Indique um Amigo', desc: 'Compartilhe seu link pessoal. Quando um amigo se cadastrar e comprar qualquer pacote, <strong>ambos</strong> receberão um crédito de anúncio bônus.', yourLink: 'Seu Link de Indicação', copyLink: 'Copiar Link', copied: 'Copiado!', share: 'Compartilhar', scanQR: 'Escaneie para abrir o link de cadastro', bonus: 'O bônus é aplicado quando seu amigo se cadastra com este link e compra qualquer pacote.', invited: 'Amigos Convidados', bonusEarned: 'Créditos Bônus Ganhos', pending: 'Convites Pendentes', howTitle: 'Como Funciona', step1: 'Compartilhe', step1d: 'Copie seu link único e envie para um amigo.', step2: 'Eles Compram', step2d: 'Quando seu amigo se cadastra e compra um pacote, a mágica acontece.', step3: 'Ambos Ganham', step3d: 'Vocês dois recebem 1 crédito de anúncio bônus. Grátis!', yourReferrals: 'Suas Indicações', bonusEarnedBadge: 'Bônus Ganho', pendingBadge: 'Pendente', toastCopied: 'Link copiado. Compartilhe com alguém que você ama.' },
  ar: { badge: 'انشر الحب', title: 'أخبر صديقًا', desc: 'شارك رابطك الشخصي. عندما يسجل صديقك ويشتري أي حزمة، <strong>كلاكما</strong> سيحصل على رصيد إعلان إضافي.', yourLink: 'رابط الإحالة الخاص بك', copyLink: 'نسخ الرابط', copied: 'تم النسخ!', share: 'مشاركة', scanQR: 'امسح لفتح رابط التسجيل', bonus: 'يُطبَّق المكافأة عندما يسجل صديقك عبر هذا الرابط ويشتري أي حزمة.', invited: 'أصدقاء تمت دعوتهم', bonusEarned: 'أرصدة إضافية مكتسبة', pending: 'دعوات معلقة', howTitle: 'كيف يعمل', step1: 'شارك', step1d: 'انسخ رابطك الفريد وأرسله لصديق.', step2: 'يشترون', step2d: 'عندما يسجل صديقك ويشتري حزمة، يحدث السحر.', step3: 'كلاكما يربح', step3d: 'كلاكما يحصل على رصيد إعلان إضافي واحد. مجاني!', yourReferrals: 'إحالاتك', bonusEarnedBadge: 'مكافأة مكتسبة', pendingBadge: 'معلق', toastCopied: 'تم نسخ الرابط. شاركه مع شخص تهتم به.' },
  ja: { badge: '愛を広めよう', title: '友達に教える', desc: '個人リンクを共有しましょう。友達が登録してバンドルを購入すると、<strong>お二人とも</strong>ボーナスアナウンスメントクレジットがもらえます。', yourLink: 'あなたの紹介リンク', copyLink: 'リンクをコピー', copied: 'コピーしました！', share: '共有', scanQR: 'スキャンして登録リンクを開く', bonus: '友達がこのリンクで登録し、有料バンドルを購入するとボーナスが適用されます。', invited: '招待した友達', bonusEarned: '獲得ボーナスクレジット', pending: '保留中の招待', howTitle: '使い方', step1: '共有', step1d: 'ユニークなリンクをコピーして友達に送りましょう。', step2: '友達が購入', step2d: '友達が登録してバンドルを購入すると、魔法が起きます。', step3: '両方が獲得', step3d: 'お二人とも1つのボーナスアナウンスメントクレジットがもらえます。無料！', yourReferrals: 'あなたの紹介', bonusEarnedBadge: 'ボーナス獲得', pendingBadge: '保留中', toastCopied: 'リンクをコピーしました。大切な人に共有してください。' },
  ko: { badge: '사랑을 나누세요', title: '친구에게 알리기', desc: '개인 링크를 공유하세요. 친구가 가입하고 번들을 구매하면 <strong>두 분 모두</strong> 보너스 어나운스먼트 크레딧을 받습니다.', yourLink: '추천 링크', copyLink: '링크 복사', copied: '복사됨!', share: '공유', scanQR: '스캔하여 가입 링크 열기', bonus: '친구가 이 링크로 가입하고 유료 번들을 구매하면 보너스가 적용됩니다.', invited: '초대한 친구', bonusEarned: '획득한 보너스 크레딧', pending: '대기 중인 초대', howTitle: '이용 방법', step1: '공유', step1d: '고유 링크를 복사하여 친구에게 보내세요.', step2: '친구가 구매', step2d: '친구가 가입하고 번들을 구매하면 마법이 일어납니다.', step3: '둘 다 획득', step3d: '두 분 모두 보너스 어나운스먼트 크레딧 1개를 받습니다. 무료!', yourReferrals: '내 추천', bonusEarnedBadge: '보너스 획득', pendingBadge: '대기 중', toastCopied: '링크가 복사되었습니다. 소중한 사람에게 공유하세요.' },
  id: { badge: 'SEBARKAN KASIH', title: 'Beritahu Teman', desc: 'Bagikan tautan pribadi Anda. Ketika teman mendaftar dan membeli paket apa pun, Anda <strong>berdua</strong> akan menerima kredit pengumuman bonus.', yourLink: 'Tautan Referensi Anda', copyLink: 'Salin Tautan', copied: 'Disalin!', share: 'Bagikan', scanQR: 'Pindai untuk membuka tautan pendaftaran', bonus: 'Bonus berlaku saat teman Anda mendaftar menggunakan tautan ini dan membeli paket berbayar.', invited: 'Teman Diundang', bonusEarned: 'Kredit Bonus Diperoleh', pending: 'Undangan Tertunda', howTitle: 'Cara Kerja', step1: 'Bagikan', step1d: 'Salin tautan unik Anda dan kirim ke teman.', step2: 'Mereka Membeli', step2d: 'Ketika teman Anda mendaftar dan membeli paket, keajaiban terjadi.', step3: 'Keduanya Mendapat', step3d: 'Anda berdua menerima 1 kredit pengumuman bonus. Gratis!', yourReferrals: 'Referensi Anda', bonusEarnedBadge: 'Bonus Diperoleh', pendingBadge: 'Tertunda', toastCopied: 'Tautan disalin. Bagikan dengan seseorang yang Anda sayangi.' },
  hi: { badge: 'प्यार बांटें', title: 'किसी दोस्त को बताएं', desc: 'अपना व्यक्तिगत लिंक साझा करें। जब कोई दोस्त साइन अप करे और कोई भी बंडल खरीदे, तो आप <strong>दोनों</strong> को बोनस क्रेडिट मिलेगा।', yourLink: 'आपका रेफ़रल लिंक', copyLink: 'लिंक कॉपी करें', copied: 'कॉपी हो गया!', share: 'साझा करें', scanQR: 'साइन अप लिंक खोलने के लिए स्कैन करें', bonus: 'बोनस तब लागू होता है जब आपका दोस्त इस लिंक से साइन अप करे और कोई पेड बंडल खरीदे।', invited: 'आमंत्रित दोस्त', bonusEarned: 'अर्जित बोनस क्रेडिट', pending: 'लंबित आमंत्रण', howTitle: 'यह कैसे काम करता है', step1: 'साझा करें', step1d: 'अपना अनूठा लिंक कॉपी करें और किसी दोस्त को भेजें।', step2: 'वे खरीदें', step2d: 'जब आपका दोस्त साइन अप करे और बंडल खरीदे, तो जादू होता है।', step3: 'दोनों कमाएं', step3d: 'आप दोनों को 1 बोनस क्रेडिट मिलता है। मुफ़्त!', yourReferrals: 'आपके रेफ़रल', bonusEarnedBadge: 'बोनस अर्जित', pendingBadge: 'लंबित', toastCopied: 'लिंक कॉपी हो गया। किसी अपने को भेजें।' },
};

interface Referral {
  id: string;
  referred_name: string;
  referred_email: string;
  status: string;
  bonus_granted: boolean;
  created_at: string;
}

export default function ReferAFriend() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const c = lc(referCopy, language);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      const [profileRes, referralsRes] = await Promise.all([
        supabase.from('profiles').select('referral_code').eq('user_id', user.id).single(),
        supabase.from('referrals').select('*').eq('referrer_id', user.id).order('created_at', { ascending: false }),
      ]);
      if (profileRes.data) setReferralCode((profileRes.data as any).referral_code);
      if (referralsRes.data) setReferrals(referralsRes.data as unknown as Referral[]);
    };
    fetchData();
  }, [user]);

  const referralLink = referralCode
    ? `${window.location.origin}/?ref=${referralCode}`
    : '';

  const handleCopy = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success(c.toastCopied);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = referralLink;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      toast.success(c.toastCopied);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleNativeShare = async () => {
    if (!referralLink) return;
    // Check if Web Share API is truly available (not just declared)
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Say It NOW — Tell a Friend',
          text: 'I use Say It NOW to share meaningful messages with the people I love. Sign up and we both get a bonus credit!',
          url: referralLink,
        });
        return;
      } catch (err: any) {
        // If user cancelled, do nothing. If unsupported, fall through to copy.
        if (err?.name === 'AbortError') return;
      }
    }
    // Fallback: just copy the link
    handleCopy();
  };

  const bonusEarned = referrals.filter((r) => r.bonus_granted).length;
  const pendingCount = referrals.filter((r) => !r.bonus_granted).length;

  return (
    <div className="min-h-screen flex flex-col relative z-0">
      <div
        className="fixed inset-0 -z-10 pointer-events-none opacity-30 dark:opacity-25"
        style={{
          backgroundImage: `url(${peaceBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <AuthenticatedHeader />

      <main className="flex-1">
        {/* Hero */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-hero-gradient" />
          <div className="container relative z-10 py-14 md:py-20 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
                {c.badge}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 tracking-tight">
                {c.title}
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed" dangerouslySetInnerHTML={{ __html: c.desc }} />
            </motion.div>
          </div>
        </div>

        <div className="container max-w-4xl py-10">
          <div className="grid gap-8 md:grid-cols-5">
            {/* Share link — 3 cols */}
            <motion.div
              className="md:col-span-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card className="glass border-0 rounded-2xl shadow-lg">
                <CardContent className="p-8">
                  <div className="flex items-center gap-2 mb-6">
                    <LinkIcon className="h-5 w-5 text-primary" />
                    <h2 className="text-xl font-semibold text-foreground">{c.yourLink}</h2>
                  </div>

                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={referralLink}
                      className="rounded-xl bg-muted/50 text-sm font-mono"
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                    <Button
                      size="icon"
                      variant="outline"
                      className="rounded-xl shrink-0"
                      onClick={handleCopy}
                      aria-label="Copy link"
                    >
                      {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>

                  <div className="flex gap-3 mt-5">
                    <Button
                      onClick={handleCopy}
                      className="flex-1 rounded-xl gap-2 shadow-lg shadow-primary/20"
                    >
                      <Copy className="h-4 w-4" />
                      {copied ? c.copied : c.copyLink}
                    </Button>

                    <Button
                      variant="outline"
                      onClick={() => setShareOpen(true)}
                      className="rounded-xl gap-2"
                    >
                      <Share2 className="h-4 w-4" />
                      {c.share}
                    </Button>

                    <Button
                      variant="outline"
                      onClick={() => setShowQR(!showQR)}
                      className="rounded-xl gap-2"
                      aria-label="Toggle QR code"
                    >
                      <QrCode className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* QR Code */}
                  {showQR && referralLink && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-6 flex flex-col items-center gap-3"
                    >
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <QRCodeSVG
                          value={referralLink}
                          size={180}
                          level="M"
                          bgColor="#ffffff"
                          fgColor="#1a1a1a"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {c.scanQR}
                      </p>
                    </motion.div>
                  )}

                  <p className="text-xs text-muted-foreground text-center mt-5">
                    {c.bonus}
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Stats — 2 cols */}
            <motion.div
              className="md:col-span-2 space-y-4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Card className="glass border-0 rounded-2xl">
                <CardContent className="p-6 text-center">
                  <Users className="h-8 w-8 text-primary mx-auto mb-2" />
                  <p className="text-3xl font-bold text-foreground">{referrals.length}</p>
                  <p className="text-sm text-muted-foreground">{c.invited}</p>
                </CardContent>
              </Card>

              <Card className="glass border-0 rounded-2xl">
                <CardContent className="p-6 text-center">
                  <Sparkles className="h-8 w-8 text-primary mx-auto mb-2" />
                  <p className="text-3xl font-bold text-foreground">{bonusEarned}</p>
                  <p className="text-sm text-muted-foreground">{c.bonusEarned}</p>
                </CardContent>
              </Card>

              <Card className="glass border-0 rounded-2xl">
                <CardContent className="p-6 text-center">
                  <Heart className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-foreground">{pendingCount}</p>
                  <p className="text-sm text-muted-foreground">{c.pending}</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* How it works */}
          <motion.div
            className="mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
             <h3 className="text-lg font-semibold text-foreground mb-6 text-center">{c.howTitle}</h3>
            <div className="grid gap-6 md:grid-cols-3">
              {[
                { icon: LinkIcon, title: c.step1, desc: c.step1d },
                { icon: Gift, title: c.step2, desc: c.step2d },
                { icon: CheckCircle, title: c.step3, desc: c.step3d },
              ].map((step, i) => (
                <Card key={i} className="glass border-0 rounded-2xl text-center">
                  <CardContent className="p-6">
                    <step.icon className="h-8 w-8 text-primary mx-auto mb-3" />
                    <h4 className="font-semibold text-foreground mb-1">{step.title}</h4>
                    <p className="text-sm text-muted-foreground">{step.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>

          {/* Referral history */}
          {referrals.length > 0 && (
            <motion.div
              className="mt-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <h3 className="text-lg font-semibold text-foreground mb-4">{c.yourReferrals}</h3>
              <div className="space-y-3">
                {referrals.map((ref) => (
                  <Card key={ref.id} className="glass border-0 rounded-xl">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">{ref.referred_name || ref.referred_email}</p>
                        <p className="text-sm text-muted-foreground capitalize">{ref.status.replace('_', ' ')}</p>
                      </div>
                      <div>
                        {ref.bonus_granted ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
                            <CheckCircle className="h-3 w-3" /> {c.bonusEarnedBadge}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                            {c.pendingBadge}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </main>

      <Footer />

      <ReferralShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        referralLink={referralLink}
      />
    </div>
  );
}
